import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  FC,
} from "react";
import { useRoutes } from "react-router-dom";
import router from "src/router";
import { IntlProvider } from "react-intl";
import { AuthProvider, AuthContext } from "./contexts/JWTAuthContext";
import { CssBaseline } from "@mui/material";
import ThemeProvider from "./theme/ThemeProvider";
import { ThemeModeProvider } from "./contexts/ThemeContext";
import { messages, supportedLanguages } from "./translations/translations";
import { loadCourseData } from "./api/api";

export const LocalizationContext = createContext({
  userLanguage: "",
  setLanguage: (_newLanguage: string) => {},
  profileModulesMap: {},
  submodulesMap: {},
  modules: [],
  submodules: [],
});

async function loadDynamicTranslations() {
  try {
    const manifestResponse = await fetch("/content/translations/manifest.json");
    const { files } = await manifestResponse.json();

    const translations = await Promise.all(
      files.map(async (file: string) => {
        const response = await fetch(`/content/translations/${file}`);
        return response.json();
      }),
    );

    return translations.reduce(
      (acc, translation) => {
        Object.keys(translation).forEach((lang) => {
          if (!acc[lang]) acc[lang] = {};
          acc[lang] = { ...acc[lang], ...translation[lang] };
        });
        return acc;
      },
      {} as Record<string, any>,
    );
  } catch (error) {
    console.error("Error loading dynamic translations:", error);
    return {};
  }
}

interface CourseDataLoaderProps {
  setProfileModulesMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setSubmodulesMap: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setModules: React.Dispatch<React.SetStateAction<string[]>>;
  setSubmodules: React.Dispatch<React.SetStateAction<string[]>>;
}

const CourseDataLoader: FC<CourseDataLoaderProps> = ({
  setProfileModulesMap,
  setSubmodulesMap,
  setModules,
  setSubmodules,
}) => {
  const { uid } = useContext(AuthContext);

  useEffect(() => {
    if (!uid) {
      setProfileModulesMap({});
      setSubmodulesMap({});
      setModules([]);
      setSubmodules([]);
      return;
    }

    const loadData = async () => {
      const [profileModulesMapData, submodulesMapData] = await Promise.all([
        loadCourseData<Record<string, string[]>>("profileModulesMap"),
        loadCourseData<Record<string, string[]>>("submodulesMap"),
      ]);
      setProfileModulesMap(profileModulesMapData);
      setSubmodulesMap(submodulesMapData);
      setModules(Object.keys(submodulesMapData));
      setSubmodules(Object.values(submodulesMapData).flat());
    };

    loadData();
  }, [uid, setProfileModulesMap, setSubmodulesMap, setModules, setSubmodules]);

  return null;
};

function App() {
  const content = useRoutes(router);
  const [userLanguage, setUserLanguage] = useState("pt");
  const [combinedMessages, setCombinedMessages] = useState(messages);
  const [profileModulesMap, setProfileModulesMap] = useState<
    Record<string, string[]>
  >({});
  const [submodulesMap, setSubmodulesMap] = useState<Record<string, string[]>>(
    {},
  );
  const [modules, setModules] = useState([]);
  const [submodules, setSubmodules] = useState([]);



  useEffect(() => {
    const initializeTranslations = async () => {
      const dynamicTranslations = await loadDynamicTranslations();
      const merged = Object.keys(messages).reduce(
        (acc, lang) => {
          acc[lang] = {
            ...messages[lang],
            ...(dynamicTranslations[lang] || {}),
          };
          return acc;
        },
        {} as typeof messages,
      );

      setCombinedMessages(merged);
    };

    initializeTranslations();
  }, []);

  const setLanguage = (language: string) => {
    if (Object.keys(supportedLanguages).includes(language)) {
      setUserLanguage(language);
    }
  };

  return (
    <ThemeModeProvider>
      <ThemeProvider>
        <CssBaseline />
        <AuthProvider setLanguage={setLanguage}>
          <LocalizationContext.Provider
            value={{
              userLanguage,
              setLanguage,
              profileModulesMap,
            submodulesMap,
            modules,
            submodules,
          }}
        >
          <CourseDataLoader
            setProfileModulesMap={setProfileModulesMap}
            setSubmodulesMap={setSubmodulesMap}
            setModules={setModules}
            setSubmodules={setSubmodules}
          />
          <IntlProvider
            locale={userLanguage}
            messages={combinedMessages[userLanguage] || {}}
            onError={(err) => {
              if (err.code === "MISSING_TRANSLATION") return;
              console.error(err);
            }}
          >
            {content}
          </IntlProvider>
        </LocalizationContext.Provider>
        </AuthProvider>
      </ThemeProvider>
    </ThemeModeProvider>
  );
}

export default App;
